import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { getAppText } from 'src/i18n';
import { showMessage } from '../../components/components/SnackbarNotification/snackbarNotificationSlice';

export interface GroupSizeDto {
    [key: string]: any;
    id: number;
    nameFr: string;
    nameEn: string;
    region_Id: number;
}

export interface GroupSizeState {
    groupSizes: GroupSizeDto[];
    groupSize: GroupSizeDto;
    gettingGroupSizes: boolean;
    gettingGroupSize: boolean;
    archivingGroupSize: boolean;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error?: string;
}

const blankGroupSizeDto: GroupSizeDto = {
    id: 0,
    nameFr: '',
    nameEn: '',
    region_Id: 0,
};

const initialState: GroupSizeState = {
    groupSizes: [],
    groupSize: blankGroupSizeDto,
    gettingGroupSizes: false,
    gettingGroupSize: false,
    archivingGroupSize: false,
    status: 'idle',
    error: undefined,
};

export const groupSizeSlice = createSlice({
    name: 'groupSizes',
    initialState,
    reducers: {
        clearGroupSizes: (state) => {
            state.groupSizes = [];
            state.groupSize = blankGroupSizeDto;
        },
        updateGroupSize: (state, param) => {
            const { payload } = param;
            state.groupSize = payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getGroupSizes.pending, (state, action) => {
                state.gettingGroupSizes = true;
                state.status = 'loading';
            })
            .addCase(getGroupSizes.fulfilled, (state, action) => {
                state.gettingGroupSizes = false;
                state.groupSizes = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getGroupSizes.rejected, (state, action) => {
                state.gettingGroupSizes = false;
                state.groupSizes = [];
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(getGroupSize.pending, (state, action) => {
                state.gettingGroupSize = true;
                state.status = 'loading';
            })
            .addCase(getGroupSize.fulfilled, (state, action) => {
                state.gettingGroupSize = false;
                state.groupSize = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getGroupSize.rejected, (state, action) => {
                state.gettingGroupSize = false;
                state.groupSize = blankGroupSizeDto;
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(archiveGroupSize.pending, (state, action) => {
                state.archivingGroupSize = true;
                state.status = 'loading';
            })
            .addCase(archiveGroupSize.fulfilled, (state, action) => {
                state.archivingGroupSize = false;
                state.groupSizes = state.groupSizes.filter((x) => x.id !== action.payload.id);
                state.status = 'succeeded';
            })
            .addCase(archiveGroupSize.rejected, (state, action) => {
                state.archivingGroupSize = false;
                state.status = 'failed';
                state.error = action.error.message;
            });
    },
});

export const { clearGroupSizes } = groupSizeSlice.actions;
export const { updateGroupSize } = groupSizeSlice.actions;

export const getGroupSizes = createAsyncThunk('groupSizes/getGroupSizes', async (_, thunkAPI) => {
    const response = await axios.get(`/api/synergy/group-sizes`);

    return response.data;
});

export const getGroupSize = createAsyncThunk('groupSizes/getGroupSize', async (id: number, thunkAPI) => {
    const response = await axios.get(`/api/synergy/group-sizes/${id}`);

    return response.data;
});

export const archiveGroupSize = createAsyncThunk('groupSizes/archiveGroupSize', async (id: number, thunkAPI) => {
    const response = await axios.post(`/api/synergy/group-sizes/archive/${id}`);
    thunkAPI.dispatch(
        showMessage({
            type: 'info',
            message: getAppText('archivedGroupSize'),
        })
    );
    return response.data;
});

export const saveGroupSize = createAsyncThunk('groupSizes/saveGroupSize', async (groupSizeToSave: GroupSizeDto, thunkAPI) => {
    if (groupSizeToSave.id === 0) {
        const response = await axios.post(`/api/synergy/group-sizes/create`, groupSizeToSave);
        thunkAPI.dispatch(
            showMessage({
                type: 'info',
                message: getAppText('createGroupSize'),
            })
        );
        return response.data;
    } else {
        const response = await axios.put(`/api/synergy/group-sizes/put`, groupSizeToSave);
        thunkAPI.dispatch(
            showMessage({
                type: 'info',
                message: getAppText('saveGroupSize'),
            })
        );
        return response.data;
    }
});
